import gsap from 'gsap'

/**
 * Page transition — bottom-center badge with circular loader:
 *
 *   1. SHOW     Badge rises in
 *   2. LOAD     Accent ring draws around the mark
 *   3. SWAP     Route changes (badge stays)
 *   4. HIDE     Badge exits
 */

const SHOW = 0.22
const LOADER = 0.4
const BEAT = 0.04
const HIDE = 0.16

/** Circle path length for r=46 in a 100×100 viewBox */
const LOADER_LEN = 2 * Math.PI * 46

function els(overlay: HTMLElement) {
  const badge = overlay.querySelector<HTMLElement>('.barba-badge')
  const mark = overlay.querySelector<HTMLElement>('.barba-mark')
  const loader = overlay.querySelector<SVGElement>('.barba-loader')
  const progress = overlay.querySelector<SVGCircleElement>('.barba-loader-progress')

  if (!badge || !mark || !loader || !progress) {
    throw new Error('Barba overlay elements missing')
  }

  return { badge, mark, loader, progress }
}

export function resetOverlay(overlay: HTMLElement): void {
  const { badge, mark, loader, progress } = els(overlay)

  gsap.killTweensOf([overlay, badge, mark, loader, progress])
  gsap.set(overlay, { autoAlpha: 0, pointerEvents: 'none' })
  gsap.set(badge, { autoAlpha: 0, y: 72, scale: 0.94 })
  gsap.set(mark, { autoAlpha: 1 })
  gsap.set(loader, { rotation: -90, transformOrigin: '50% 50%', opacity: 1 })
  gsap.set(progress, {
    strokeDasharray: LOADER_LEN,
    strokeDashoffset: LOADER_LEN,
    opacity: 1,
  })
}

/** Show the badge and run the circular loader. */
export async function playLeave(overlay: HTMLElement): Promise<void> {
  const { badge, mark, loader, progress } = els(overlay)

  gsap.killTweensOf([overlay, badge, mark, loader, progress])

  gsap.set(overlay, { autoAlpha: 1, visibility: 'visible', pointerEvents: 'none' })
  gsap.set(badge, { autoAlpha: 0, y: 72, scale: 0.94 })
  gsap.set(mark, { autoAlpha: 1 })
  gsap.set(loader, { rotation: -90, transformOrigin: '50% 50%', opacity: 1 })
  gsap.set(progress, {
    strokeDasharray: LOADER_LEN,
    strokeDashoffset: LOADER_LEN,
    opacity: 1,
  })

  const tl = gsap.timeline()

  // Slide up from below the viewport edge
  tl.to(badge, {
    autoAlpha: 1,
    y: 0,
    scale: 1,
    duration: SHOW,
    ease: 'power3.out',
  }, 0)

  tl.to(progress, {
    strokeDashoffset: 0,
    duration: LOADER,
    ease: 'power2.inOut',
  }, SHOW * 0.35)

  await tl
}

/** Brief beat after route swap, then hide the badge. */
export async function playEnter(overlay: HTMLElement): Promise<void> {
  const { badge } = els(overlay)

  try {
    const tl = gsap.timeline()

    tl.to({}, { duration: BEAT }, 0)

    tl.to(badge, {
      autoAlpha: 0,
      y: 56,
      scale: 0.94,
      duration: HIDE,
      ease: 'power2.in',
    }, BEAT)

    tl.set(overlay, { autoAlpha: 0 })

    await tl
  } finally {
    resetOverlay(overlay)
  }
}
